/**
 * Domain types for earn vault list/detail (GET `/earn/vaults` and GET `/earn/vaults/:address`).
 * One file per earn route family — see {@link ./earn-positions}, {@link ./earn-queue},
 * {@link ./earn-events} for the other endpoints.
 */

import { Replace } from "../../reducers/type-util/primitive";
import { EarnVaultSnapshotApi, EarnVaultWithLatestSnapshotApi } from "../api/earn";
import { ParsedEarnAccount } from "./earn-accounts";
import { LendingVaultMetadata } from "./vault";

export * from "./earn-accounts";

/**
 * Latest row from `earn_vault_snapshot`. Field names mirror the BE camelCase wire shape;
 * the numeric units shift by layer:
 *   - Parser output (this type, as cached): raw protocol units — `bigint` lamports for
 *     u64 atomic fields, raw bps / x100 for the small u32 fields.
 *   - After `convertEarnVaultRecord` (`utils/view/earn.ts`) the view hook emits
 *     `EarnVaultSnapshotView`: u64 atomic fields become `TokenAmount`; bps/x100 fields rescale
 *     to base-10 decimals (0.5 for 50%, 1.5 multiplier for leverage).
 */
export type EarnVaultSnapshot = Replace<
    EarnVaultSnapshotApi,
    {
        timestamp: number;
        snapshotSlot: bigint;
        shareSupply: bigint;
        totalAumAssets: bigint;
        pendingWithdrawalsAssets: bigint;
    }
>;

/** Earn vault row after `parseEarnVaultFromApi` (GET `/earn/vaults`). */
export type EarnVaultRecord = Replace<
    EarnVaultWithLatestSnapshotApi,
    {
        metadataJson: LendingVaultMetadata;
        acceptedAssetMints: string[];
        latestSnapshot: EarnVaultSnapshot | null;
        lastUpdatedSlot?: number;
        /** Provider-keyed decoded on-chain account. v1: only Exponent populates `exponent`. */
        parsedAccountJson: ParsedEarnAccount;
    }
>;

export enum EarnProviderKind {
    Exponent = 0,
    Glam = 1,
    Upshift = 2,
    LoopscaleVault = 3
}

/** Mirrors BE `EarnVaultStatus` u8 discriminant. */
export enum EarnVaultStatus {
    Draft = 0,
    Active = 1,
    Paused = 2,
    Deprecated = 3
}
